import { Link } from "react-router-dom";
import { useState } from "react";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { Handshake, Building2, Landmark, Trophy, HeartHandshake, Loader2, Check, School, ArrowRight } from "lucide-react";

const PARTNER_TYPES = ["School", "Recreation Center", "City / Municipality", "Sponsor", "Funder"];

const REASONS = [
  { icon: Building2, title: "Schools & Centers", text: "Bring structured SEAA enrichment and performance readiness to your students and members." },
  { icon: Landmark, title: "Cities & Municipalities", text: "Expand youth opportunity and out-of-school-time programming across your community." },
  { icon: Trophy, title: "Sponsors", text: "Power supplies, instructors, and student experiences while building your community impact." },
  { icon: HeartHandshake, title: "Funders & Grants", text: "Invest in measurable, structured outcomes for underserved youth ages 8–16." },
];

export default function Partner() {
  const [form, setForm] = useState({ organization: "", contact_name: "", email: "", phone: "", partner_type: "School", message: "" });
  const [loading, setLoading] = useState(false);
  const [done, setDone] = useState(false);

  const update = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await api.post("/partners", form);
      setDone(true);
      toast.success("Thank you! We'll be in touch soon.");
    } catch (err) {
      toast.error("Something went wrong. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div data-testid="partner-page">
      <section className="bg-brandnavy">
        <div className="mx-auto max-w-7xl px-5 sm:px-8 py-20 sm:py-24">
          <span className="text-xs font-semibold tracking-[0.2em] uppercase text-accent">Partner With Us</span>
          <h1 className="mt-4 font-display text-4xl sm:text-5xl font-black tracking-tight text-white max-w-3xl">
            Let's build something meaningful together.
          </h1>
          <p className="mt-6 max-w-2xl text-lg text-slate-300 leading-relaxed">
            We work with schools, recreation centers, cities, sponsors, and funders who believe early exposure builds
            confidence, confidence builds capability, and capability builds futures.
          </p>
        </div>
      </section>

      <section className="mx-auto max-w-7xl px-5 sm:px-8 pt-16">
        <Link
          to="/afterschool"
          data-testid="partner-afterschool-callout"
          className="group flex flex-col gap-4 rounded-3xl border-l-4 bg-white p-6 shadow-sm transition-all hover:-translate-y-1 hover:shadow-lg sm:flex-row sm:items-center sm:justify-between"
          style={{ borderColor: "#8BC53F" }}
        >
          <div className="flex items-start gap-4">
            <span className="grid h-12 w-12 shrink-0 place-items-center rounded-2xl text-brandnavy" style={{ background: "#8BC53F22" }}><School className="h-6 w-6" style={{ color: "#8BC53F" }} /></span>
            <div>
              <p className="text-xs font-semibold uppercase tracking-wide" style={{ color: "#8BC53F" }}>New: Champions × LP Foundation</p>
              <h3 className="mt-1 font-display text-lg font-bold text-brandnavy">Feed Their Future — complete afterschool program</h3>
              <p className="mt-1 text-sm text-slate-600">Meals, fitness, SEAA enrichment &amp; workforce readiness for your students.</p>
            </div>
          </div>
          <span className="inline-flex items-center gap-1.5 text-sm font-semibold text-brandnavy">Learn more <ArrowRight className="h-4 w-4 transition-transform group-hover:translate-x-1" /></span>
        </Link>
      </section>

      <section className="mx-auto max-w-7xl px-5 sm:px-8 py-20 grid lg:grid-cols-12 gap-12">
        <div className="lg:col-span-5">
          <h2 className="font-display text-2xl sm:text-3xl font-black text-brandnavy">Who we partner with</h2>
          <div className="mt-8 space-y-5">
            {REASONS.map((r) => (
              <div key={r.title} className="flex gap-4">
                <span className="grid h-12 w-12 shrink-0 place-items-center rounded-2xl bg-orange-50 text-accent">
                  <r.icon className="h-6 w-6" />
                </span>
                <div>
                  <h3 className="font-display font-bold text-brandnavy">{r.title}</h3>
                  <p className="mt-1 text-sm text-slate-600 leading-relaxed">{r.text}</p>
                </div>
              </div>
            ))}
          </div>
        </div>

        <div className="lg:col-span-7">
          <div className="rounded-3xl border border-slate-100 bg-white p-6 sm:p-8 shadow-lg">
            {done ? (
              <div data-testid="partner-success" className="py-12 text-center">
                <span className="mx-auto grid h-16 w-16 place-items-center rounded-full bg-green-100 text-green-600">
                  <Check className="h-8 w-8" />
                </span>
                <h3 className="mt-5 font-display text-2xl font-black text-brandnavy">Inquiry received!</h3>
                <p className="mt-2 text-slate-600">Thank you for your interest in partnering with LP Foundation. Our team will reach out shortly.</p>
              </div>
            ) : (
              <form onSubmit={submit} className="space-y-4" data-testid="partner-form">
                <div className="flex items-center gap-2 text-accent">
                  <Handshake className="h-5 w-5" />
                  <h3 className="font-display text-xl font-bold text-brandnavy">Partnership Inquiry</h3>
                </div>
                <div className="grid sm:grid-cols-2 gap-4">
                  <Field label="Organization *"><input data-testid="partner-organization" required value={form.organization} onChange={update("organization")} className={inputCls} /></Field>
                  <Field label="Contact Name *"><input data-testid="partner-contact-name" required value={form.contact_name} onChange={update("contact_name")} className={inputCls} /></Field>
                  <Field label="Email *"><input data-testid="partner-email" type="email" required value={form.email} onChange={update("email")} className={inputCls} /></Field>
                  <Field label="Phone"><input data-testid="partner-phone" value={form.phone} onChange={update("phone")} className={inputCls} /></Field>
                </div>
                <Field label="Partnership Type *">
                  <select data-testid="partner-type" value={form.partner_type} onChange={update("partner_type")} className={inputCls}>
                    {PARTNER_TYPES.map((t) => <option key={t} value={t}>{t}</option>)}
                  </select>
                </Field>
                <Field label="How would you like to partner? *">
                  <textarea data-testid="partner-message" required rows={4} value={form.message} onChange={update("message")} className={inputCls} />
                </Field>
                <button data-testid="partner-submit" disabled={loading} className="w-full inline-flex items-center justify-center gap-2 rounded-full bg-brandnavy px-6 py-4 font-semibold text-white transition-all hover:-translate-y-0.5 disabled:opacity-60">
                  {loading ? <Loader2 className="h-5 w-5 animate-spin" /> : <Handshake className="h-5 w-5" />}
                  Submit Inquiry
                </button>
              </form>
            )}
          </div>
        </div>
      </section>
    </div>
  );
}

const inputCls = "w-full rounded-xl border border-slate-200 px-4 py-3 text-sm outline-none transition focus:ring-2 focus:ring-accent/40 focus:border-accent";
function Field({ label, children }) {
  return (
    <label className="block">
      <span className="block text-xs font-semibold uppercase tracking-wide text-slate-500 mb-1.5">{label}</span>
      {children}
    </label>
  );
}
